import { useEffect, useState, useCallback } from "react";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import GlucoseChart from "../components/GlucoseChart";
import StatusBadge, { classifyGlucose } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import GlucoseForm from "../forms/GlucoseForm";
import RecordActions from "../components/RecordActions";

export default function Glucose() {
  const { t } = useI18n();
  const { activeProfileId, activeProfile, can } = useProfile();
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const [g, tg] = await Promise.all([
      api.get(`/profiles/${activeProfileId}/glucose`),
      api.get(`/profiles/${activeProfileId}/targets`),
    ]);
    setRows(g.data);
    setTarget(tg.data.current);
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  const chartData = [...rows].slice(0, 30).reverse();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#0D253F]">{t("glucose_title")}</h1>
          <p className="text-[#627D98] mt-1">{t("glucose_sub")}</p>
        </div>
        {can("log_glucose") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold shrink-0" data-testid="new-reading-btn">
                <Plus size={18} className="mr-1" /> {t("new_reading")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-head text-xl">{t("new_reading")}</DialogTitle></DialogHeader>
              <GlucoseForm onSaved={() => { setOpen(false); load(); }} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="df-card p-5 min-w-0">
        <GlucoseChart readings={chartData} target={target} unit={activeProfile?.glucose_unit} />
        <p className="text-xs text-[#627D98] mt-2">{t("manual_note")}</p>
      </div>

      <div className="df-card p-5">
        {rows.length === 0 ? (
          <p className="text-sm text-[#627D98]">{t("no_glucose")}</p>
        ) : (
          <>
          <div className="sm:hidden divide-y divide-[#F0F5FA]" data-testid="glucose-mobile-list">
            {rows.map((r) => {
              const st = classifyGlucose(r.value, r.unit, target);
              return (
                <div key={r.id} className="py-3 space-y-1" data-testid={`glucose-mrow-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-[#0D253F] whitespace-nowrap">{r.value} {r.unit}</span>
                      {st && <StatusBadge status={st} />}
                    </span>
                    <RecordActions record={r} canEdit={can("correct_records")} onEdit={setEditRow} testid={`glucose-m-${r.id}`} />
                  </div>
                  <div className="text-xs text-[#627D98] truncate">
                    {new Date(r.datetime).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    {" · "}{r.source === "sensor" ? t("src_sensor") : t("src_capillary")}{" · "}{r.author_name}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="hidden sm:block overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#627D98] text-xs uppercase">
                  <th className="py-2 pr-4">{t("g_table_dt")}</th>
                  <th className="py-2 pr-4">{t("g_table_value")}</th>
                  <th className="py-2 pr-4"></th>
                  <th className="py-2 pr-4">{t("g_table_source")}</th>
                  <th className="py-2">{t("g_table_author")}</th>
                  <th className="py-2 text-right">{t("actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F5FA]">
                {rows.map((r) => {
                  const st = classifyGlucose(r.value, r.unit, target);
                  return (
                    <tr key={r.id} data-testid={`glucose-row-${r.id}`}>
                      <td className="py-2.5 pr-4 text-[#3B526B]">{new Date(r.datetime).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="py-2.5 pr-4 font-semibold text-[#0D253F]">{r.value} {r.unit}</td>
                      <td className="py-2.5 pr-4">{st && <StatusBadge status={st} />}</td>
                      <td className="py-2.5 pr-4 text-[#3B526B]">{r.source === "sensor" ? t("src_sensor") : t("src_capillary")}</td>
                      <td className="py-2.5 text-[#627D98]">{r.author_name}</td>
                      <td className="py-2.5 text-right"><RecordActions record={r} canEdit={can("correct_records")} onEdit={setEditRow} testid={`glucose-${r.id}`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {editRow && (
        <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-head text-xl">{t("edit")} · {t("glucose_title")}</DialogTitle></DialogHeader>
            <GlucoseForm record={editRow} onSaved={() => { setEditRow(null); load(); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
